/**
 * HTTP-клиент для Field PWA.
 * - Authorization: Bearer <accessToken>
 * - Авто-refresh при 401, повтор запроса
 * - X-Client: field-app в заголовках (для отображения сессии в админке)
 * - При окончательной потере авторизации — onAuthLost
 */
import type { ApiError, RefreshResponse } from '@jaluzi/types';
import { clearAuth } from './storage.js';
import { i18n } from './i18n.js';

let accessToken: string | null = null;
let sessionGeneration = 0;
let refreshInFlight: Promise<string | null> | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
  sessionGeneration += 1;
}

export function getAccessToken() {
  return accessToken;
}

export function onAuthLostHandler(fn: (() => void) | null) {
  onAuthLost = fn;
}

export class HttpError extends Error {
  constructor(public status: number, public payload: ApiError) {
    super(typeof payload.message === 'string' ? payload.message : payload.message.join(', '));
  }
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  const generation = sessionGeneration;
  refreshInFlight = (async () => {
    try {
      const r = await fetch('/v1/auth/refresh', {
        signal: AbortSignal.timeout(45_000),
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client': 'field-app' },
        credentials: 'same-origin',
        body: '{}',
      });
      if (generation !== sessionGeneration) return null;
      if (!r.ok) {
        if (r.status === 401 || r.status === 403) {
          clearAuth();
          accessToken = null;
          onAuthLost?.();
        }
        return null;
      }
      const data = (await r.json()) as RefreshResponse;
      if (generation !== sessionGeneration) return null;
      accessToken = data.accessToken;
      return data.accessToken;
    } catch {
      // Сеть упала — не разлогиниваем. Дадим UI разобраться.
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export interface ApiOptions extends RequestInit {
  /** Если false — не подставляем Bearer (для login/refresh endpoint-ов). */
  auth?: boolean;
  /** Не запускать refresh после 401 (служебный logout). */
  authRefresh?: boolean;
  /** Идемпотентность — для outbox-мутаций. */
  idempotencyKey?: string;
  /** Не позволяет отправить отложенную запись под другой учётной записью. */
  expectedUserId?: string;
}

export async function api<T = unknown>(path: string, init: ApiOptions = {}): Promise<T> {
  const { auth = true, authRefresh = true, headers = {}, idempotencyKey, expectedUserId, ...rest } = init;

  const generation = sessionGeneration;
  const doFetch = async (token: string | null): Promise<Response> => {
    if (sessionGeneration !== generation) throw new HttpError(401, { statusCode: 401, message: 'session_changed' });
    if (expectedUserId && token) {
      let subject: unknown;
      try { subject = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))).sub; } catch { /* invalid token */ }
      if (subject !== expectedUserId) throw new HttpError(401, { statusCode: 401, message: 'session_changed' });
    }
    const finalHeaders: Record<string, string> = {
      ...(rest.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      'X-Client': 'field-app',
      ...(headers as Record<string, string>),
    };
    if (auth && token) finalHeaders['Authorization'] = `Bearer ${token}`;
    if (idempotencyKey) finalHeaders['Idempotency-Key'] = idempotencyKey;
    return fetch(path, { ...rest, signal: rest.signal ?? AbortSignal.timeout(45_000), headers: finalHeaders });
  };

  let response = await doFetch(accessToken);

  if (sessionGeneration !== generation) throw new HttpError(401, { statusCode: 401, message: 'session_changed' });
  if (response.status === 401 && auth && authRefresh && !path.endsWith('/auth/refresh')) {
    const newToken = await refreshAccessToken();
    if (newToken) response = await doFetch(newToken);
  }

  if (!response.ok) {
    let payload: ApiError;
    try {
      payload = (await response.json()) as ApiError;
    } catch {
      payload = { statusCode: response.status, message: response.statusText };
    }
    throw new HttpError(response.status, payload);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/**
 * Скачать бинарный файл (PDF, xlsx) с тем же auth-flow что у `api`.
 * Имя файла берём из Content-Disposition, иначе fallbackName.
 * Отдельный helper вместо `api`, потому что нужен blob, а не JSON.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const doFetch = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { 'X-Client': 'field-app' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(path, { headers });
  };

  let response = await doFetch(accessToken);
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) response = await doFetch(newToken);
  }

  if (!response.ok) {
    let payload: ApiError;
    try {
      payload = (await response.json()) as ApiError;
    } catch {
      payload = { statusCode: response.status, message: response.statusText };
    }
    throw new HttpError(response.status, payload);
  }

  const blob = await response.blob();
  const cd = response.headers.get('Content-Disposition') ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const name = m ? m[1]! : fallbackName;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Достать человеческое сообщение из ошибки API. Используется в toast'ах. */
export function humanizeError(e: unknown): string {
  if (e instanceof HttpError) {
    const raw = e.payload.message;
    // Бэк иногда возвращает структурированный объект { message, hint, ... }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const obj = raw as { message?: string; hint?: string };
      const code = obj.message;
      const tr = code ? errText(code) : null;
      if (tr) return tr;
      // hint (человеческая подсказка с бэка) приоритетнее сырого кода.
      return obj.hint ?? (code ? prettyFallback(code) : i18n.t('errors._unknown'));
    }
    // class-validator шлёт МАССИВ фраз («slatSizeMm must not be greater than 10000»).
    // Переводим каждую в человеческий текст, а не склеиваем сырьё.
    if (Array.isArray(raw)) {
      const human = raw.map((s) => humanizeValidation(String(s))).filter((s): s is string => !!s);
      if (human.length) return human.join('. ');
    }
    const m = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw.join(', ') : '';
    const tr = errText(m);
    if (tr) return tr;
    const validation = humanizeValidation(m);
    if (validation) return validation;
    // Перевода для кода нет — но бэк мог приложить готовую подсказку рядом с кодом
    // (например, с точными суммами: сколько нужно / сколько принято). Показываем её
    // вместо общего «не получилось». (см. ApiError.hint)
    if (typeof e.payload.hint === 'string' && e.payload.hint.trim()) return e.payload.hint;
    return prettyFallback(m);
  }
  if (e instanceof Error) return e.message;
  return i18n.t('errors._unknown');
}

/** Перевод кода ошибки из i18n (errors.<code>). null, если перевода нет. */
function errText(code: string): string | null {
  if (!code) return null;
  const key = `errors.${code}`;
  const tr = i18n.t(key);
  return tr && tr !== key ? tr : null;
}

/**
 * Предохранитель: непокрытый код-«snake_case» НЕ показываем сырым — отдаём
 * общий понятный текст (локализованный). Как и англоязычную ругань валидатора,
 * которую не удалось разобрать: «must be an integer number» замерщику ни о чём.
 */
function prettyFallback(msg: string): string {
  if (/^[a-z][a-z0-9_]*$/.test(msg)) return i18n.t('errors._fallback');
  if (/\bmust\b/.test(msg)) return i18n.t('errors._fallback');
  return msg;
}

/**
 * Поля, о которых валидатор бэка может ругаться. Лимиты в DTO заданы в ММ, а вводит
 * замерщик МЕТРЫ (кроме борта и количества) — поэтому «100000» надо показать как «100 м»,
 * иначе цифра в ошибке не совпадает с цифрой в поле. См. calc.controller.ts ItemDto.
 */
const FIELD_META: Record<string, { labelKey: string; unitKey: string; toHuman: (mm: number) => number }> = {
  widthMm: { labelKey: 'errors._fieldWidth', unitKey: 'errors._unitM', toHuman: (mm) => mm / 1000 },
  heightMm: { labelKey: 'errors._fieldHeight', unitKey: 'errors._unitM', toHuman: (mm) => mm / 1000 },
  slatSizeMm: { labelKey: 'errors._fieldSlat', unitKey: 'errors._unitM', toHuman: (mm) => mm / 1000 },
  bortSizeMm: { labelKey: 'errors._fieldBort', unitKey: 'errors._unitMm', toHuman: (mm) => mm },
  quantity: { labelKey: 'errors._fieldQty', unitKey: 'errors._unitPcs', toHuman: (n) => n },
};

/** «100000» → «100 000», «0.001» → «0,001» (группировка и разделитель по языку). */
const fmtNum = (n: number): string =>
  new Intl.NumberFormat(i18n.language || 'ru', { maximumFractionDigits: 3 }).format(n);

/**
 * «slatSizeMm must not be greater than 10000» → «Размер планки: не больше 10 м».
 * null, если фраза незнакомая — тогда сработает prettyFallback.
 */
function humanizeValidation(msg: string): string | null {
  const m = /^(\w+) must not be (greater|less) than (\d+)$/.exec(msg.trim());
  if (!m) return null;
  const [, field, dir, rawLimit] = m;
  const meta = FIELD_META[field ?? ''];
  if (!meta) return null;
  const limit = fmtNum(meta.toHuman(Number(rawLimit)));
  return i18n.t(dir === 'greater' ? 'errors._maxValue' : 'errors._minValue', {
    field: i18n.t(meta.labelKey),
    limit,
    unit: i18n.t(meta.unitKey),
  });
}
